import { useEffect, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { CaretLeft, Heart } from "@phosphor-icons/react";
import { toast } from "sonner";
import { fetchReport } from "@/lib/dailyReports";
import { getChildById } from "@/lib/dailyReportsDemo";
import { prettyDate } from "@/lib/entryTypes";
import { listFoodMealUpdatesForChild, mergeFoodEntriesIntoDay } from "@/lib/foodProgram";
import {
  listReportPhotos,
  reportPhotoSignedUrl,
} from "@/lib/storage";
import { isSupabaseConfigured } from "@/lib/supabase";
import EntryCard from "@/components/reports/EntryCard";
import FileUploadCard from "@/components/upload/FileUploadCard";

export default function ParentActivityDetail() {
  const { date } = useParams();
  const [search] = useSearchParams();
  const childId = search.get("child");
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [photos, setPhotos] = useState([]);

  useEffect(() => {
    if (!childId || !date) return;
    (async () => {
      setLoading(true);
      try {
        const [r, foodEntries] = await Promise.all([
          fetchReport(childId, date),
          listFoodMealUpdatesForChild(childId, { since: date, until: date, limit: 20 }),
        ]);
        setReport(mergeFoodEntriesIntoDay(r, foodEntries, date));
        if (r?.id && isSupabaseConfigured) {
          try {
            const ps = await listReportPhotos(r.id);
            setPhotos(ps);
          } catch {
            setPhotos([]);
          }
        } else {
          setPhotos([]);
        }
      } catch (err) {
        toast.error(err.message || "Could not load that day");
      } finally {
        setLoading(false);
      }
    })();
  }, [childId, date]);

  const child = childId ? getChildById(childId) : null;
  const entries = (report?.entries || [])
    .slice()
    .sort((a, b) => a.occurred_at.localeCompare(b.occurred_at));

  if (loading) {
    return (
      <div
        data-testid="parent-day-loading"
        className="flex items-center justify-center py-16"
      >
        <div className="h-8 w-8 rounded-full border-2 border-primary/30 border-t-primary animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="parent-day-detail" className="space-y-5">
      <Link
        to="/parent/activity"
        data-testid="parent-day-back"
        className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-600 hover:text-neutral-900"
      >
        <CaretLeft size={16} weight="bold" />
        Back
      </Link>

      <header>
        <p className="text-xs font-bold uppercase tracking-[0.15em] text-neutral-500">
          {child ? `${child.first_name}'s day` : "Day"}
        </p>
        <h1 className="mt-1 font-heading text-3xl font-semibold tracking-tight text-neutral-900">
          {prettyDate(date)}
        </h1>
      </header>

      {!report ? (
        <div
          data-testid="parent-day-empty"
          className="rounded-2xl border border-dashed border-neutral-200 bg-white p-10 text-center text-sm text-neutral-500"
        >
          No activity was logged for this day.
        </div>
      ) : (
        <>
          {(report.overall_mood || report.summary_note) && (
            <section
              data-testid="parent-day-summary"
              className="rounded-2xl border border-neutral-200 bg-white p-5 shadow-[0_2px_12px_rgba(20,22,26,0.04)]"
            >
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                  <Heart size={20} weight="duotone" className="text-primary-hover" />
                </div>
                <div>
                  {report.overall_mood && (
                    <p className="text-xs font-bold uppercase tracking-[0.15em] text-primary-hover">
                      Mood · {report.overall_mood}
                    </p>
                  )}
                  {report.summary_note && (
                    <p className="mt-1 text-sm leading-relaxed text-neutral-800">
                      {report.summary_note}
                    </p>
                  )}
                </div>
              </div>
            </section>
          )}

          {entries.length === 0 ? (
            <p className="text-sm text-neutral-500">No entries were logged.</p>
          ) : (
            <ul className="space-y-2" data-testid="parent-day-entries">
              {entries.map((e) => (
                <li key={e.id}>
                  <EntryCard entry={e} />
                </li>
              ))}
            </ul>
          )}

          {isSupabaseConfigured && photos.length > 0 && (
            <FileUploadCard
              testId="parent-day-photos"
              label="Photos from today"
              accept="image/*"
              mode="image"
              items={photos}
              canEdit={false}
              resolveSignedUrl={reportPhotoSignedUrl}
              onUpload={() => Promise.resolve()}
              onRemove={() => Promise.resolve()}
            />
          )}
        </>
      )}
    </div>
  );
}
